import React, { useEffect, useState } from 'react';
import authFetch from '../api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import DailyLogTable from '../components/DailyLogTable';
import { CalendarDays } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AdminDailyLogs = () => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setError(null);
        console.log('🔍 Fetching projects for daily logs...');
        
        const res = await authFetch('/projects/assigned');
        
        let projectsData;
        if (res && res.data) {
          projectsData = res.data;
        } else {
          projectsData = res;
        }
        
        console.log('📊 Projects data received:', projectsData);
        setProjects(Array.isArray(projectsData) ? projectsData : []);
        
      } catch (err) {
        console.error('❌ Failed to fetch projects:', err);
        console.error('Error details:', {
          message: err.message,
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data
        });
        setError(t('adminDaily.failedToLoadProjects'));
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    if (!selectedProject) return;
    const fetchLogs = async () => {
      setLoading(true);
      try {
        console.log('🔍 Fetching logs for project:', selectedProject._id);
        console.log('📅 Selected date:', selectedDate);
        
        const res = await authFetch(`/projects/${selectedProject._id}`);
        
        let projectData;
        if (res && res.data) {
          projectData = res.data;
        } else {
          projectData = res;
        }
        
        console.log('📊 Project data received:', projectData);
        setLogs(projectData.activities || []);
        
      } catch (err) {
        console.error('❌ Failed to fetch logs:', err);
        console.error('Error details:', {
          message: err.message,
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data
        });
        setLogs([]);
      }
      setLoading(false);
    };
    fetchLogs();
  }, [selectedProject, selectedDate]);

  // Debug information
  console.log('🔍 AdminDailyLogs Debug:', {
    projectsCount: projects.length,
    selectedProject: selectedProject?.name,
    selectedDate,
    logsCount: logs.length,
    loading,
    error
  });

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center pt-16">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('adminDaily.dailyLogs')}</h1>
            <p className="text-gray-600 mt-2">{t('adminDaily.trackDailyActivities')}</p>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="text-center">
            <div className="text-red-600 text-lg mb-4">{error}</div>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              {t('adminDaily.tryAgain')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center pt-16">
        <div>
                      <h1 className="text-3xl font-bold text-gray-900">{t('adminDaily.dailyLogs')}</h1>
            <p className="text-gray-600 mt-2">{t('adminDaily.trackDailyActivities')}</p>
        </div>
      </div>

      {!selectedProject ? (
        <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
          <div className="p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('adminDaily.selectProject')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {Array.isArray(projects) && projects.map(project => (
                <div
                  key={project._id}
                  className="bg-gray-50 p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-colors"
                  onClick={() => setSelectedProject(project)}
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{project.name}</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">{t('adminDaily.status')}:</span>
                      {project.percentCompleted === 100
                        ? <span className="text-green-600 font-semibold text-sm">🟢 {t('adminDaily.completed')}</span>
                        : project.status === "At Risk"
                        ? <span className="text-red-600 font-semibold text-sm">🔴 {t('adminDaily.atRisk')}</span>
                        : project.status === "On Track"
                        ? <span className="text-yellow-600 font-semibold text-sm">🟡 {t('adminDaily.onTrack')}</span>
                        : <span className="text-gray-400 font-semibold text-sm">⚪ {t('adminDaily.notStarted')}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">{t('adminDaily.progress')}:</span>
                      <span className="text-sm text-gray-600">{project.percentCompleted || 0}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">{t('adminDaily.assigned')}:</span>
                      <span className="text-sm text-gray-600">
                        {project.assignedEmployees?.length > 0
                          ? project.assignedEmployees.map(emp => 
                              typeof emp === 'object' ? emp.name : `Employee ID: ${emp}`
                            ).join(', ')
                          : t('adminDaily.notAssigned')}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
          <div className="p-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
              <h2 className="text-xl font-semibold text-gray-900">{t('adminDaily.project')}: {selectedProject.name}</h2>
              <DatePicker
                selected={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                dateFormat="yyyy-MM-dd"
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="text-gray-500">{t('adminDaily.loadingLogs')}</div>
              </div>
            ) : (
              <DailyLogTable activities={logs} selectedDate={selectedDate} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDailyLogs;
